<template>
  <div class="form-page">
    <!-- Success screen: real tracking links + QR codes -->
    <n-card v-if="savedCampaign" class="form-card" data-test="success-screen">
      <template #header>
        Campanha "{{ savedCampaign.name }}" salva com sucesso!
      </template>
      <p>
        Cada estratégia tem seu próprio link de rastreamento. Compartilhe o link
        ou o QR code:
      </p>
      <div
        v-for="strategy in savedCampaign.strategies"
        :key="strategy._id"
        class="saved-strategy"
        data-test="saved-strategy"
      >
        <div class="saved-strategy-info">
          <n-tag :bordered="false" type="success">{{ strategy.type }}</n-tag>
          <span class="saved-strategy-dest">{{
            strategyTarget(strategy)
          }}</span>
        </div>
        <n-input-group>
          <n-input
            :value="trackingUrl(strategy)"
            readonly
            data-test="tracking-link"
          />
          <n-button type="primary" @click="copyLink(trackingUrl(strategy))">
            Copiar
          </n-button>
        </n-input-group>
        <QrCodeCard
          :url="trackingUrl(strategy)"
          :file-name="`${savedCampaign.name}-${strategy.type.toLowerCase()}`"
        />
      </div>
      <n-button block class="new-campaign-btn" @click="resetForm">
        Criar outra campanha
      </n-button>
    </n-card>

    <!-- Campaign form -->
    <n-card
      v-else
      class="form-card"
      :title="isEditMode ? 'Editar Campanha' : 'Nova Campanha'"
    >
      <n-spin :show="loadingEdit">
        <n-form ref="formRef" :model="campaign" :rules="campaignRules">
          <n-form-item label="Nome da campanha" path="name">
            <n-input
              v-model:value="campaign.name"
              placeholder="Ex: evento_2026"
              :disabled="isEditMode"
              data-test="campaign-name"
            />
          </n-form-item>
        </n-form>

        <n-divider>Estratégias</n-divider>

        <n-empty
          v-if="campaign.strategies.length === 0"
          description="Nenhuma estratégia adicionada ainda"
          class="strategies-empty"
        />
        <div
          v-for="(strategy, index) in campaign.strategies"
          :key="index"
          class="strategy-item"
          data-test="strategy-item"
        >
          <div class="saved-strategy-info">
            <n-tag :bordered="false" type="info">{{ strategy.type }}</n-tag>
            <span class="saved-strategy-dest">{{
              strategyTarget(strategy)
            }}</span>
            <n-tag v-if="strategy._id" :bordered="false" size="small">
              já salva
            </n-tag>
          </div>
          <n-button
            v-if="!strategy._id"
            size="small"
            type="error"
            tertiary
            data-test="remove-strategy"
            @click="removeStrategy(index)"
          >
            Remover
          </n-button>
        </div>

        <n-divider>Adicionar estratégia</n-divider>

        <n-form
          ref="strategyFormRef"
          :model="strategyDraft"
          :rules="strategyRules"
        >
          <n-form-item label="Tipo de estratégia" path="type">
            <n-select
              v-model:value="strategyDraft.type"
              :options="strategyTypeOptions"
              placeholder="Selecione o tipo"
              data-test="strategy-type"
            />
          </n-form-item>

          <template v-if="strategyDraft.type === 'REDIRECT'">
            <n-form-item label="URL de redirecionamento" path="url">
              <n-input
                v-model:value="strategyDraft.url"
                placeholder="Ex: https://instagram.com/sua_conta"
                data-test="redirect-url"
              />
            </n-form-item>
            <n-alert
              v-if="httpWarning"
              type="warning"
              class="http-warning"
              data-test="http-warning"
            >
              Este destino usa http:// (sem criptografia). O redirecionamento
              funciona, mas prefira https:// quando disponível.
            </n-alert>
            <n-form-item label="Tipo de conteúdo" path="contentType">
              <n-select
                v-model:value="strategyDraft.contentType"
                :options="contentTypeOptions"
                placeholder="Selecione"
                data-test="redirect-content-type"
              />
            </n-form-item>
          </template>

          <template v-else-if="strategyDraft.type === 'IMAGE'">
            <n-form-item label="URL da imagem" path="url">
              <n-input
                v-model:value="strategyDraft.url"
                placeholder="https://..."
              />
            </n-form-item>
          </template>

          <template v-else-if="strategyDraft.type === 'VIDEO'">
            <n-form-item label="Link do YouTube" path="url">
              <n-input
                v-model:value="strategyDraft.url"
                placeholder="https://youtube.com/..."
              />
            </n-form-item>
          </template>

          <n-alert v-if="strategyDraft.type === 'REWARD'" type="error">
            O tipo Recompensa ainda não foi desenvolvido.
          </n-alert>

          <n-button
            v-if="strategyDraft.type && strategyDraft.type !== 'REWARD'"
            type="primary"
            data-test="add-strategy"
            @click="addStrategy"
          >
            Adicionar estratégia
          </n-button>
        </n-form>

        <n-divider />

        <n-button
          type="primary"
          block
          size="large"
          :loading="saving"
          data-test="save-campaign"
          @click="save"
        >
          {{ isEditMode ? "Salvar novas estratégias" : "Salvar Campanha" }}
        </n-button>
      </n-spin>
    </n-card>
  </div>
</template>

<script>
import {
  addStrategyToCampaign,
  getCampaignById,
  saveCampaign,
} from "../services/api";
import { contentTypes, campaignEnum } from "../helpers";
import {
  isPlainHttp,
  normalizeRedirectUrl,
  buildTrackingUrl,
} from "../helpers/url";
import QrCodeCard from "../components/QrCodeCard.vue";

export default {
  name: "FormPage",
  components: { QrCodeCard },
  data() {
    const urlRule = {
      required: true,
      trigger: ["blur", "input"],
      validator: (rule, value) => {
        if (!value) return new Error("Informe a URL");
        if (!normalizeRedirectUrl(value)) {
          return new Error(
            "URL inválida. Use um endereço http(s) válido, ex: https://site.com",
          );
        }
        return true;
      },
    };

    return {
      campaign: { name: "", strategies: [] },
      strategyDraft: { type: null, url: "", contentType: null },
      savedCampaign: null,
      saving: false,
      loadingEdit: false,
      editCampaignId: this.$route.query.edit || null,
      campaignRules: {
        name: {
          required: true,
          trigger: ["blur", "input"],
          message: "Informe o nome da campanha",
        },
      },
      strategyRules: {
        type: {
          required: true,
          trigger: "change",
          message: "Selecione o tipo",
        },
        url: urlRule,
        contentType: {
          required: true,
          trigger: "change",
          message: "Selecione o tipo de conteúdo",
        },
      },
      strategyTypeOptions: campaignEnum.map((item) => ({
        label: item.text,
        value: item.enum,
      })),
      contentTypeOptions: contentTypes.map((item) => ({
        label: item.text,
        value: item.type,
      })),
    };
  },
  computed: {
    isEditMode() {
      return Boolean(this.editCampaignId);
    },
    httpWarning() {
      return isPlainHttp(this.strategyDraft.url);
    },
  },
  async mounted() {
    if (this.isEditMode) {
      await this.loadCampaignForEdit();
    }
  },
  methods: {
    async loadCampaignForEdit() {
      this.loadingEdit = true;
      try {
        const existing = await getCampaignById(this.editCampaignId);
        if (!existing || !existing._id) {
          throw new Error("not found");
        }
        this.campaign = {
          name: existing.name,
          // _id present -> already persisted, shown but not removable
          strategies: existing.strategies.map((s) => ({ ...s })),
        };
      } catch (err) {
        window.$message?.error("Campanha não encontrada para edição.");
        this.$router.replace("/form");
        this.editCampaignId = null;
      } finally {
        this.loadingEdit = false;
      }
    },

    strategyTarget(strategy) {
      return strategy.data?.youtube_url || strategy.data?.url || "";
    },

    trackingUrl(strategy) {
      return buildTrackingUrl(this.savedCampaign._id, strategy._id);
    },

    async addStrategy() {
      const type = this.strategyDraft.type;
      if (!type || type === "REWARD") return;

      try {
        await this.$refs.strategyFormRef.validate();
      } catch {
        return;
      }

      const normalizedUrl = normalizeRedirectUrl(this.strategyDraft.url);
      let data;
      if (type === "REDIRECT") {
        data = {
          url: normalizedUrl,
          contentType: this.strategyDraft.contentType,
        };
      } else if (type === "IMAGE") {
        data = { url: normalizedUrl, height: 198, width: 300 };
      } else {
        data = { youtube_url: normalizedUrl, height: 1211, width: 681 };
      }

      this.campaign.strategies.push({ type, data });
      // Full draft reset: type, url AND contentType (the old form left the
      // content-type select showing a stale value here)
      this.strategyDraft = { type: null, url: "", contentType: null };
    },

    removeStrategy(index) {
      this.campaign.strategies.splice(index, 1);
    },

    async save() {
      // Re-entrancy guard: n-button's loading prop doesn't block clicks, and
      // a double submit here would create a duplicate campaign. The flag MUST
      // be set synchronously before any await (validate() yields) — a second
      // click during validation would otherwise slip through.
      if (this.saving) return;
      this.saving = true;
      try {
        try {
          await this.$refs.formRef.validate();
        } catch {
          return;
        }

        const newStrategies = this.campaign.strategies.filter((s) => !s._id);
        if (newStrategies.length === 0) {
          window.$message?.warning(
            this.isEditMode
              ? "Adicione pelo menos uma nova estratégia."
              : "Adicione pelo menos uma estratégia!",
          );
          return;
        }

        if (this.isEditMode) {
          let updated = null;
          for (const strategy of newStrategies) {
            const result = await addStrategyToCampaign(
              this.editCampaignId,
              strategy,
            );
            updated = result.campaign;
            // Mark the draft as persisted in place: on a mid-loop failure the
            // saved ones show "já salva" (and are filtered from a retry)
            // while still-unsaved drafts stay editable instead of vanishing
            const saved = updated.strategies[updated.strategies.length - 1];
            if (saved && saved._id) {
              strategy._id = saved._id;
            }
          }
          this.savedCampaign = updated;
        } else {
          const payload = {
            name: this.campaign.name.replace(/\s/g, "_"),
            strategies: this.campaign.strategies,
          };
          this.savedCampaign = await saveCampaign(payload);
        }
        window.$message?.success("Campanha salva com sucesso!");
      } catch (err) {
        const apiError = err.response?.data?.error;
        window.$message?.error(
          apiError
            ? `Não conseguimos salvar: ${apiError}`
            : "Não conseguimos salvar essa campanha. Tente novamente.",
        );
      } finally {
        this.saving = false;
      }
    },

    async copyLink(url) {
      try {
        await navigator.clipboard.writeText(url);
        window.$message?.success("Link copiado!");
      } catch {
        window.$message?.error("Não foi possível copiar o link.");
      }
    },

    resetForm() {
      this.savedCampaign = null;
      this.campaign = { name: "", strategies: [] };
      this.strategyDraft = { type: null, url: "", contentType: null };
      this.editCampaignId = null;
      if (this.$route.query.edit) {
        this.$router.replace("/form");
      }
    },
  },
};
</script>

<style scoped>
.form-page {
  display: flex;
  justify-content: center;
  padding: 30px 20px;
}

.form-card {
  max-width: 720px;
  width: 100%;
}

.strategy-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background-color: #1c1127;
  border-radius: 6px;
}

.saved-strategy {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #1c1127;
  border-radius: 8px;
}

.saved-strategy-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.saved-strategy-dest {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.85;
  font-size: 13px;
}

.strategies-empty {
  margin: 15px 0;
}

.http-warning {
  margin-bottom: 15px;
}

.new-campaign-btn {
  margin-top: 10px;
}
</style>
